import {
   clearChannelSearch,
   getConversationChannelData,
   setChannelIsSearching,
   setChannelSearchIndex,
   setChannelSearchResults,
   setChannelSearchText,
} from '../../Slice';
import Store from '../../Store';

const searchState = {
   timer: null,
   filteredIndices: [],
   currentIndex: 0,
   elementCache: new Map(),
   container: null,
};

const getContainer = () => {
   if (!searchState.container || !document.contains(searchState.container)) {
      searchState.container = document.getElementById('conversationList');
   }
   return searchState.container;
};

const getElement = messageId => {
   let el = searchState.elementCache.get(messageId);
   if (el && !document.contains(el)) {
      searchState.elementCache.delete(messageId);
      el = null;
   }
   if (!el) {
      el = document.getElementById(messageId);
      if (el) searchState.elementCache.set(messageId, el);
   }
   return el;
};

const isAtTop = () => {
   const container = getContainer();
   if (!container) return false;
   const { scrollTop, scrollHeight } = container;
   const distance = scrollTop >= 0 ? scrollTop : scrollHeight + scrollTop;
   return distance < 50;
};

const matchesSearch = (msg, searchText) => {
   if (msg.type !== 'message') return false;
   const { messageType, content } = msg;
   const escapedSearch = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
   const regexPattern = new RegExp(`(^|\\s)(${escapedSearch})`, 'i');

   if (messageType === 'text') {
      return regexPattern.test(content?.message);
   }
   const media = content?.media;
   if (!media) return false;
   if (messageType === 'file') {
      return regexPattern.test(media.fileName);
   }
   if (messageType === 'image' || messageType === 'video') {
      return regexPattern.test(media.caption);
   }
   return false;
};

const findClosestIndex = (results, fromCenter = false) => {
   if (!results.length) return 0;
   const container = getContainer();
   if (!container) return 0;
   const containerRect = container.getBoundingClientRect();
   let closest = 0;
   let minDist = Infinity;
   const target = fromCenter ? containerRect.top + (containerRect.height >> 1) : containerRect.top;
   for (let i = 0; i < results.length; i++) {
      const el = getElement(results[i].messageId);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const point = fromCenter ? rect.top + (rect.height >> 1) : rect.top;
      const dist = Math.abs(point - target);
      if (dist < minDist) {
         minDist = dist;
         closest = i;
      }
   }
   return closest;
};

export const findChannelMessageAndUpdate = ({ channelId, searchText, resetCurrentIndex = false }) => {
   const trimmed = searchText.toLowerCase();
   if (!trimmed) return { message: 'No search value' };
   searchState.filteredIndices = [];
   const messages = getConversationChannelData(channelId)?.messages;
   if (!messages?.length) return { message: 'No messages' };
   for (let i = 0; i < messages.length; i++) {
      if (matchesSearch(messages[i], trimmed)) {
         searchState.filteredIndices.push({
            index: i,
            messageId: messages[i].messageId,
         });
      }
   }
   if (!searchState.filteredIndices.length) {
      return { message: 'No message found' };
   }
   if (resetCurrentIndex) {
      if (isAtTop()) {
         searchState.currentIndex = 0;
      } else {
         searchState.currentIndex = findClosestIndex(searchState.filteredIndices, true);
      }
   }
   return searchState.filteredIndices[searchState.currentIndex] || { message: 'No message found' };
};

const updateReduxState = channelId => {
   Store.dispatch(setChannelSearchResults({ channelId, searchResults: searchState.filteredIndices }));
   Store.dispatch(setChannelSearchIndex({ channelId, index: searchState.currentIndex }));
};

export const handleFindNextChannelMessage = ({ channelId, direction }) => {
   const step = direction === 'upward' ? 1 : -1;
   const newIdx = searchState.currentIndex + step;
   if (newIdx < 0 || newIdx >= searchState.filteredIndices.length) {
      return { message: 'No results found' };
   }
   const target = searchState.filteredIndices[newIdx];
   searchState.currentIndex = newIdx;
   updateReduxState(channelId);
   scrollToChannelMessage({ messageId: target.messageId, channelId });
   return { messageId: target.messageId, index: target.index };
};

const highlightMessage = element => {
   setTimeout(() => element.classList.remove('search-highlight-active'), 1000);
};

export const scrollToChannelMessage = ({ messageId, scrollContainerId = 'conversationList', channelId }) => {
   // Try to use VirtualizedList's scrollToMessage if available (for virtualized lists)
   if (channelId && scrollContainerId === 'conversationList') {
      const virtualizedListRef = Store.getState().draftData.virtualizedListRefs?.[channelId];
      if (virtualizedListRef?.scrollToMessage) {
         const result = virtualizedListRef.scrollToMessage(messageId, {
            smooth: true,
            block: 'center',
            highlight: true,
         });
         if (result !== undefined) {
            return true;
         }
      }
   }

   // Fallback to DOM-based scrollIntoView for non-virtualized lists or if VirtualizedList is not available
   const msgEl = getElement(messageId);
   const container =
      scrollContainerId === 'conversationList' ? getContainer() : document.getElementById(scrollContainerId);
   if (!msgEl || !container) return false;
   requestAnimationFrame(() => {
      msgEl.scrollIntoView({
         behavior: 'smooth',
         block: 'center',
         inline: 'nearest',
      });
      highlightMessage(msgEl);
   });
   return true;
};

export const debouncedChannelSearch = ({ channelId, text, delay = 300, scrollToMessage = true }) => {
   return new Promise(resolve => {
      clearTimeout(searchState.timer);
      searchState.timer = setTimeout(() => {
         const result = findChannelMessageAndUpdate({
            channelId,
            searchText: text,
            resetCurrentIndex: scrollToMessage,
         });
         if (result?.messageId) {
            Store.dispatch(setChannelSearchText({ channelId, searchText: text }));
            updateReduxState(channelId);
            if (scrollToMessage) {
               scrollToChannelMessage({ messageId: result.messageId, channelId });
            }
         } else {
            Store.dispatch(setChannelSearchResults({ channelId, searchResults: [] }));
            Store.dispatch(setChannelSearchIndex({ channelId, index: -1 }));
         }
         resolve(result);
      }, delay);
   });
};

export const clearChannelSearchData = (channelId, isClear = true) => {
   clearTimeout(searchState.timer);
   searchState.filteredIndices = [];
   searchState.currentIndex = 0;
   searchState.elementCache.clear();
   searchState.container = null;
   Store.dispatch(clearChannelSearch({ channelId }));
   isClear && Store.dispatch(setChannelIsSearching({ channelId, isSearching: false }));
};

const escapeRegex = str => str.replace(/[-[\]{}()*+!<=:?.\\^$|#\s,]/g, '\\$&');

export const getHighlightedText = (name, highlight) => {
   if (!name || !highlight) return name;

   const clean = highlight.replace(/\\/g, '');
   if (!clean) return name;

   const escaped = escapeRegex(clean);
   const regex = new RegExp(`^(${escaped})`, 'i');
   const match = name.match(regex);

   if (!match) return name;
   return (
      <>
         <span className="search-match-text">{match[1]}</span>
         <span>{name.substring(match[1].length)}</span>
      </>
   );
};
